import '@sorare/core/src/polyfills';

// eslint-disable-next-line import-x/order
import { createBrowserRouter } from './sentry';

import '@sorare/core/src/style/style.css';
import './remove-child-workaround';

import { useCallback, useMemo, useReducer } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { RouterProvider } from 'react-router';

import { DeviceFingerPrintProvider } from '@sorare/core/src/contexts/deviceFingerprint/Provider';
import { FeatureFlagsProvider } from '@sorare/core/src/contexts/featureFlags/Provider';
import { GraphqlProvider } from '@sorare/core/src/contexts/graphql/Provider';
import { IntlProvider } from '@sorare/core/src/contexts/intl/Provider';
import { SentryProvider } from '@sorare/core/src/contexts/sentry/Provider';
import { withProfiler } from '@sorare/core/src/contexts/sentry/sentry';
import { SnackNotificationProvider } from '@sorare/core/src/contexts/snackNotification/Provider';
import { TMProvider } from '@sorare/core/src/contexts/tm/Provider';
import { useBasenameLocale } from '@sorare/core/src/hooks/useBasenameLocale';
import { reactErrorHandler } from '@sorare/core/src/lib/error';
import { withFFProvider } from '@sorare/core/src/lib/featureFlags';

import { EphemeralLink, isEphemeralLink } from './components/EphemeralLink';
import { ViteErrorHandler } from './components/ViteErrorHandler';
import { routeObjects } from './routeObjects';

const RootProviders = ({
  locale,
  children,
  basename,
}: Pick<ComponentProps<typeof IntlProvider>, 'locale'> & {
  children: ReactNode;
  basename?: string;
}) => {
  return (
    <SentryProvider>
      <DeviceFingerPrintProvider>
        <TMProvider>
          <GraphqlProvider
            locale={locale}
            basename={basename}
            routeObjects={routeObjects}
          >
            {children}
          </GraphqlProvider>
        </TMProvider>
      </DeviceFingerPrintProvider>
    </SentryProvider>
  );
};

const ApolloClientRouter = ({
  basename,
  locale,
  setLocale,
}: {
  basename?: string;
} & Pick<ComponentProps<typeof IntlProvider>, 'locale' | 'setLocale'>) => {
  // We need to wait for apollo client to be initialized before we can create
  // the browser router, as this will launch requests.
  const router = useMemo(
    () =>
      createBrowserRouter(routeObjects, {
        basename,
      }),
    [basename]
  );

  return (
    <IntlProvider locale={locale} setLocale={setLocale}>
      <SnackNotificationProvider>
        <HelmetProvider>
          <RouterProvider router={router} key={basename} />
        </HelmetProvider>
      </SnackNotificationProvider>
    </IntlProvider>
  );
};

const Root = () => {
  const { basename, locale, setLocale } = useBasenameLocale();

  return (
    <RootProviders locale={locale} basename={basename}>
      <ApolloClientRouter
        key={basename}
        basename={basename}
        locale={locale}
        setLocale={setLocale}
      />
    </RootProviders>
  );
};

const EphemeralLinkOrRoot = () => {
  const [, forceUpdate] = useReducer(() => ({}), {});

  const navigateTo = useCallback((path: string) => {
    window.history.replaceState({}, '', path);
    forceUpdate();
  }, []);

  // eslint-disable-next-line no-restricted-properties
  return isEphemeralLink(window.location.pathname) ? (
    <RootProviders locale="en">
      <IntlProvider locale="en" setLocale={() => {}}>
        <EphemeralLink navigateTo={navigateTo} />
      </IntlProvider>
    </RootProviders>
  ) : (
    <Root />
  );
};

const RootWithProfiler = withProfiler(EphemeralLinkOrRoot);

const container = document.getElementById('root');
const root = createRoot(container!, {
  onCaughtError: reactErrorHandler('caught'),
  onUncaughtError: reactErrorHandler('uncaught'),
  onRecoverableError: reactErrorHandler('recoverable'),
});
const App = () => (
  <FeatureFlagsProvider>
    <ViteErrorHandler />
    <RootWithProfiler />
  </FeatureFlagsProvider>
);
const FFProvider = withFFProvider()(App);
root.render(<FFProvider />);

// unregister any service worker left-over (see https://gitlab.com/sorare/frontend/-/merge_requests/3852)
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      registration.unregister();
    });
  });
}
